<template>
  <div id="orders-profit-by-month">
    <v-card class="px-4 pt-2 pb-4 my-0 elevation-0">
      <v-card-title class="px-1">
        <h1 class="card-title">
          <template v-if="showDateTypeSelector">
            {{ $t(`views.dashboardDetail.orders.ordersProfitByMonth.title`) }}
            <v-chip-group v-model="dateType" active-class="primary" mandatory column class="ml-3">
              <v-chip filter value="month" small class="mx-0">
                {{ $t('common.month') }}
              </v-chip>
              <span class="mx-1">/</span>
              <v-chip filter value="year" small class="mx-0">
                {{ $t('common.year') }}
              </v-chip>
            </v-chip-group>
          </template>
          <template v-else>
            {{ $t(`views.dashboardDetail.orders.ordersByMonth.title`) }}
            {{ $t(`common.${dateType}`) }}
          </template>
        </h1>
        <v-spacer />
        <ShowCashButton
          v-show="!filters.positionType"
          class="mt-1"
          :showCash="showCash"
          @showCashChanged="showCashChanged"
        />
      </v-card-title>
      <v-card-text class="mt-4 pa-0">
        <BarLoading v-if="!showTable && !loadedData" />
        <BarChart
          v-if="loadedData"
          ref="barChart"
          :chartData="barChartData"
          :chartOptions="barChartOptions"
        />

        <template v-if="showTable">
          <div class="my-5" />
          <v-data-table
            id="orders-profit-by-month-table"
            :headers="headers"
            :items="mySummary"
            class="elevation-0 left-sticky-table"
            mobile-breakpoint="0"
            :loading="!loadedData"
            :items-per-page="5"
            :page.sync="currentPage"
            :item-class="itemRowBackground"
            :dense="user.preferences.denseTables"
          >
            <template v-slot:item._id="{ item }">
              <div class="items-flex">
                <span>
                  <template v-if="dateType === 'month'">
                    <strong>{{ $t(`common.months.${item._id.month}`) }} {{ item._id.year }}</strong>
                  </template>
                  <template v-else>
                    <strong>{{ item._id }}</strong>
                  </template>
                </span>
                <div v-if="somePositionPendingRecreate(item)" class="ml-3">
                  <TableCoreRecreating :text="$t('views.common.tableCoreRecreating.textTotal')" />
                </div>
                <template v-else-if="someNullExchangeRate(item)">
                  <div class="ml-3">
                    <WarningIcon :text="$t(`views.dashboardDetail.orders.ordersByMonth.warning`)" />
                  </div>
                </template>
              </div>
            </template>
            <template v-slot:item.totalReceivedWithComissionBaseCurrency="{ item }">
              <currency
                v-if="item.totalReceivedWithComissionBaseCurrency"
                :value="item.totalReceivedWithComissionBaseCurrency"
                :currency="user.baseCurrency"
              />
              <span v-else></span>
            </template>
            <template v-slot:item.totalCostWithComissionBaseCurrency="{ item }">
              <currency
                v-if="item.totalCostWithComissionBaseCurrency"
                :value="item.totalCostWithComissionBaseCurrency"
                :currency="user.baseCurrency"
              />
              <span v-else></span>
            </template>
            <template v-slot:item.profitWithComissionBaseCurrency="{ item }">
              <currency
                :value="item.profitWithComissionBaseCurrency || 0"
                :currency="user.baseCurrency"
                :text-green="item.profitWithComissionBaseCurrency > 0"
                :text-red="item.profitWithComissionBaseCurrency < 0"
                :negative="item.profitWithComissionBaseCurrency < 0"
                strong
              />
            </template>
            <template v-slot:item.destinationRetentionBaseCurrency="{ item }">
              <currency
                :value="item.destinationRetentionBaseCurrency || 0"
                :currency="user.baseCurrency"
              />
            </template>
          </v-data-table>
        </template>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { map } from 'lodash'
import { mapState } from 'vuex'
import { getOrderProfitSummary } from '@/lib/api.js'
import { DARK_DEFAULT_PALETTE, DEFAULT_PALETTE } from '@/lib/colors.js'
import { getCurrencyValueAsString, getCurrencySymbol } from '@/lib/utils/currencies.js'
import Currency from '@/views/components/common/Currency'
import BarChart from '@/views/components/core/chartjs/BarChart'
import BarLoading from '@/views/components/core/BarLoading'
import WarningIcon from '@/views/components/common/WarningIcon.vue'
import TableCoreRecreating from '@/views/components/common/positions/TableCoreRecreating'
import ShowCashButton from '@/views/components/common/ShowCashButton'

export default {
  components: { Currency, BarChart, BarLoading, WarningIcon, TableCoreRecreating, ShowCashButton },
  props: {
    showTable: {
      type: Boolean,
      default: true,
    },
    showDateTypeSelector: {
      type: Boolean,
      default: true,
    },
  },
  data: () => ({
    summary: null,
    mySummary: [],
    totalRow: null,
    currentPage: null,
    loadedData: false,
    loadTimeout: null,
    dateType: 'month',
    barChartData: {
      labels: null,
      datasets: null,
    },
    filters: [],
    showCash: false,
  }),
  computed: {
    ...mapState(['user', 'hideAmounts']),
    barChartOptions() {
      const context = this
      return {
        maintainAspectRatio: false,
        legend: {
          display: false,
        },
        scales: {
          xAxes: [
            {
              gridLines: {
                display: false,
              },
            },
          ],
          yAxes: [
            {
              ticks: {
                callback: function (value) {
                  return getCurrencyValueAsString({
                    value,
                    currency: context.user.baseCurrency,
                    hide: context.hideAmounts,
                  })
                },
              },
            },
          ],
        },
        tooltips: {
          callbacks: {
            label: function (tooltipItem, data) {
              let label = data.datasets[tooltipItem.datasetIndex].label || ''
              if (label) label += ': '
              label += getCurrencyValueAsString({
                value: tooltipItem.yLabel,
                currency: context.user.baseCurrency,
                hide: context.hideAmounts,
              })
              return label
            },
          },
        },
      }
    },
    headers() {
      const headers = [
        {
          text:
            this.dateType === 'month'
              ? this.$t('views.orders.ordersTable.month')
              : this.$t('views.orders.ordersTable.year'),
          value: '_id',
          sortable: false,
          divider: true,
        },
        {
          text: `${this.$t(
            'views.common.ordersProfit.totalReceivedWithComission'
          )} (${getCurrencySymbol(this.user.baseCurrency)})`,
          value: 'totalReceivedWithComissionBaseCurrency',
          align: 'right',
        },
        {
          text: `${this.$t(
            'views.common.ordersProfit.totalCostWithComission'
          )} (${getCurrencySymbol(this.user.baseCurrency)})`,
          value: 'totalCostWithComissionBaseCurrency',
          align: 'right',
        },
        {
          text: `${this.$t('views.common.ordersProfit.profitWithComission')} (${getCurrencySymbol(
            this.user.baseCurrency
          )})`,
          value: 'profitWithComissionBaseCurrency',
          align: 'right',
        },
        {
          text: `${this.$t('views.common.ordersProfit.destinationRetention')} (${getCurrencySymbol(
            this.user.baseCurrency
          )})`,
          value: 'destinationRetentionBaseCurrency',
          align: 'right',
        },
      ]
      return headers
    },
  },
  watch: {
    dateType: {
      handler() {
        this.changeType()
      },
    },
  },
  async destroyed() {
    clearTimeout(this.loadTimeout)
  },
  methods: {
    someNullExchangeRate(item) {
      return (
        item.exchangeRates &&
        item.exchangeRates.some((elem) =>
          Array.isArray(elem) ? elem.some((elem2) => elem2 === null) : elem === null
        )
      )
    },
    somePositionPendingRecreate(item) {
      return (
        (item.coreStatus &&
          item.coreStatus.some((elem) =>
            Array.isArray(elem)
              ? elem.some((elem2) => elem2 === 'pendingRecreate')
              : elem === 'pendingRecreate'
          )) ||
        (item.corePendingRecreate &&
          item.corePendingRecreate.some((elem) =>
            Array.isArray(elem) ? elem.some((elem2) => elem2) : elem
          ))
      )
    },
    itemRowBackground(item) {
      if (this.somePositionPendingRecreate(item)) return 'row-recreating'
      else if (this.someNullExchangeRate(item)) return 'row-advise'
    },
    createBarChartObject() {
      const summary = this.mySummary.slice().reverse()

      this.barChartData.datasets = [
        {
          label: `${this.$t('views.common.ordersProfit.profitWithComission')} (${getCurrencySymbol(
            this.user.baseCurrency
          )})`,
          data: map(summary, 'profitWithComissionBaseCurrency'),
          backgroundColor: summary.map((item) =>
            item.profitWithComissionBaseCurrency < 0
              ? this.$vuetify.theme.dark
                ? DARK_DEFAULT_PALETTE.chartsBarRed
                : DEFAULT_PALETTE.chartsBarRed
              : this.$vuetify.theme.dark
              ? DARK_DEFAULT_PALETTE.chartsBarGreen
              : DEFAULT_PALETTE.chartsBarGreen
          ),
        },
      ]

      let labels
      if (this.dateType === 'month') {
        labels = map(
          summary,
          (item) =>
            this.$t(`common.months.${item._id.month}`).substring(0, 3) +
            ` ${item._id.year.toString().slice(-2)}`
        )
      } else {
        labels = map(summary, (item) => item._id)
      }
      this.barChartData.labels = labels

      if (this.$refs.barChart) this.$refs.barChart.render()
    },
    changeType() {
      const summary = JSON.parse(JSON.stringify(this.summary))

      if (summary.length && this.dateType === 'month') {
        let summaryMonths = []
        summary.forEach((year) => {
          if (year.months) summaryMonths = summaryMonths.concat(year.months)
        })
        this.mySummary = summaryMonths
      } else {
        this.mySummary = summary
      }
      this.currentPage = 1

      this.createBarChartObject()
    },
    async loadSummary(filters) {
      this.loadedData = false
      this.filters = filters
      try {
        const summary = await getOrderProfitSummary({
          group: 'byMonth',
          filter: {
            positionType: !this.showCash ? { $ne: 'cash' } : undefined,
            ...filters,
          },
        })
        this.summary = summary.data

        const isAllDate = !filters.date || Object.keys(filters.date).length === 0
        if (isAllDate) {
          this.$set(this, 'dateType', 'year')
        } else {
          this.$set(this, 'dateType', 'month')
        }

        this.changeType()

        clearTimeout(this.loadTimeout)
        if (this.summary.some((item) => this.somePositionPendingRecreate(item))) {
          this.loadTimeout = setTimeout(() => {
            this.loadSummary(filters)
          }, 5000)
        }
      } catch (error) {
        this.$root.$snackbar.checkAndOpenCommonLoadError(error)
      }
      this.loadedData = true
    },
    showCashChanged(showCash) {
      this.showCash = showCash
      this.loadSummary(this.filters)
    },
  },
}
</script>

<style lang="scss">
#orders-profit-by-month {
  .card-title {
    display: flex;
    align-items: center;
  }

  #orders-profit-by-month-table {
    .items-flex {
      display: flex;
    }
  }
}
</style>
